import {DatePicker} from 'antd';
import locale from 'antd/es/locale-provider/ru_RU';
import cn from 'classnames';
import moment, {Moment} from 'moment';
import React, {
    ForwardedRef,
    forwardRef,
    useEffect,
    useMemo, useRef,
    useState,
} from 'react';

import {ReactComponent as CloseXOutlined} from 'shared/assets/close-x-outlined.svg';
import {ReactComponent as LockIcon} from 'shared/assets/forms/lock.svg';
import {ReactComponent as CalendarIcon} from 'shared/assets/forms/time/calendar.svg';
import {
    DATE,
    DATE_FOR_REQUEST,
    DATE_FORMATS_PLACEHOLDERS_MAP,
    DEFAULT_FORMAT_PLACEHOLDER,
} from 'shared/constants/date-format';
import {getPopupContainer} from 'shared/utils';
import {convertToResolvedFormats} from 'shared/utils/date-time';

import './date-cell.less';

export const DateCell = forwardRef(({
    value,
    format,
    disabled,
    defaultValue,
    requestFormat,
    showTime,
    datePickerStates,
    errorMessage,
    fixedDropdown = false,
    disabledDateConfig,
    ...props
}: any, ref: ForwardedRef<any>) => {
    const datePickerRef = useRef<any>(null);
    const [isError, setIsError] = useState(false);
    const dateFormat = format || DATE;

    const convertedValue = useMemo(() => (
        convertToResolvedFormats(value || defaultValue)
    ), [value, defaultValue]);

    const onChangeWrapper = (newDate: Moment) => {
        props.onChange?.(newDate?.format(requestFormat ?? DATE_FOR_REQUEST));
    };

    const placeholder = useMemo(() => {
        if (typeof format === 'string' && DATE_FORMATS_PLACEHOLDERS_MAP.has(format)) {
            return DATE_FORMATS_PLACEHOLDERS_MAP.get(format);
        }
        return DEFAULT_FORMAT_PLACEHOLDER;
    }, [format]);

    const handleEnterPress = (event: any) => {
        if (event.type === 'keyup' && event.key === 'Enter'
            && datePickerRef.current && datePickerStates && datePickerStates[datePickerRef.current.props.id]) {
            setIsError(!event.target.value);
        }
    };

    const handleOnFocus = () => {
        if (datePickerRef.current && datePickerRef.current.props.id && datePickerStates) {
            datePickerStates[datePickerRef.current.props.id] = true;
        }
    };

    const handleOnBlur = () => {
        if (datePickerRef.current && datePickerRef.current.props.id && datePickerStates) {
            datePickerStates[datePickerRef.current.props.id] = false;
        }
    };

    const handleOpenChange = () => {
        if (!datePickerRef.current || !datePickerRef.current.props.id) return;

        const dateElement: HTMLElement | null = document.getElementById(datePickerRef.current.props.id);
        const inputValue = dateElement?.getAttribute('value');
        const isValid = moment(inputValue, dateFormat, true).isValid();

        setIsError(!isValid && !!inputValue);
    };

    useEffect(() => {
        if (!datePickerRef.current) {
            return undefined;
        }
        if (datePickerRef.current.props.id && datePickerStates) {
            datePickerStates[datePickerRef.current.props.id] = false;
        }
        const picker = document.getElementById(datePickerRef.current.props.id);
        if (picker) {
            picker.addEventListener('keyup', handleEnterPress);
        }
        return () => {
            if (picker) {
                picker.removeEventListener('keyup', handleEnterPress);
            }
        };
    }, []);

    return (
        <>
            <DatePicker
                {...props}
                format={[dateFormat, dateFormat.replace(/[-.:,/]/g, '')]}
                onChange={onChangeWrapper}
                onSelect={showTime ? onChangeWrapper : undefined}
                value={convertedValue}
                showTime={showTime}
                ref={props?.form ? ref || datePickerRef : datePickerRef}
                locale={locale}
                disabled={disabled}
                className={cn('date-picker', {error: isError})}
                suffixIcon={disabled ? <LockIcon /> : <CalendarIcon />}
                clearIcon={<CloseXOutlined className="clear-icon" />}
                dropdownClassName="time-picker-dropdown" // somehow it applies className to dropdown
                placeholder={placeholder}
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}
                onOpenChange={handleOpenChange}
                // чтобы dropdown сохранял позицию
                getPopupContainer={getPopupContainer(fixedDropdown)}
                disabledDate={disabledDateConfig?.disabledDate}
            />
            {isError && (
                <div
                    role="alert"
                    className="alert"
                >
                    {errorMessage || `Формат должен быть ${dateFormat}`}
                </div>
            )}
        </>
    );
});
